import { RefObject, useEffect, useRef } from 'react'
import { useMatches } from 'react-router-dom'
import { sessionStorageItems } from '~constants/session-storage-items'

type ScrollHintDirection = 'horizontal' | 'vertical'

interface UseScrollHintOptions<T extends HTMLElement> {
  trigger: boolean
  id?: string
  ref?: RefObject<T | null>
  direction?: ScrollHintDirection
}

interface UseScrollHintForElementOptions {
  getElement: () => HTMLElement | null
  trigger: boolean
  id?: string
  direction?: ScrollHintDirection
}

const scrollHintConfig = {
  /** Delay before starting animation (ms) */
  initialDelay: 400,
  /** Animation duration for each direction (ms) */
  animationDuration: 250,
  /** Max scroll distance in pixels */
  maxScrollDistance: 60,
  /** Scroll distance as fraction of container width */
  scrollDistanceRatio: 0.1,
  /** Min visible pixels to trigger hint on wider screens */
  minVisiblePixels: 200,
  /** Min visible pixels on narrow screens (< 500px width) */
  minVisiblePixelsNarrow: 100,
  /** Min visible fraction to trigger hint */
  minVisibleRatio: 0.5,
  /** How long to wait for content before giving up (ms) */
  maxWaitTime: 5000,
  /** Delay to let element render before observing (ms) */
  renderDelay: 100,
} as const

function useRouteKey(): string {
  const matches = useMatches()
  return matches.map((match) => match.id).join('-')
}

function getScrolledKeys(): Set<string> {
  try {
    const stored = sessionStorage.getItem(sessionStorageItems.scrollHintKeys)
    return stored ? new Set(JSON.parse(stored)) : new Set()
  } catch {
    return new Set()
  }
}

function addScrolledKey(key: string): void {
  try {
    const keys = getScrolledKeys()
    keys.add(key)
    sessionStorage.setItem(
      sessionStorageItems.scrollHintKeys,
      JSON.stringify([...keys]),
    )
  } catch {}
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function animateScroll(
  container: HTMLElement,
  from: number,
  to: number,
  direction: ScrollHintDirection,
): Promise<void> {
  return new Promise((resolve) => {
    const start = performance.now()
    const distance = to - from

    function step(currentTime: number) {
      if (!container.isConnected) {
        resolve()
        return
      }

      const elapsed = currentTime - start
      const progress = Math.min(elapsed / scrollHintConfig.animationDuration, 1)
      const eased = easeInOutCubic(progress)

      if (direction === 'vertical') {
        container.scrollTop = from + distance * eased
      } else {
        container.scrollLeft = from + distance * eased
      }

      if (progress < 1) {
        requestAnimationFrame(step)
      } else {
        resolve()
      }
    }

    requestAnimationFrame(step)
  })
}

async function applyScrollHint(
  container: HTMLElement,
  direction: ScrollHintDirection,
): Promise<void> {
  await new Promise((r) => setTimeout(r, scrollHintConfig.initialDelay))

  if (!container.isConnected) return

  const containerSize =
    direction === 'vertical' ? container.clientHeight : container.clientWidth
  const scrollDistance = Math.min(
    scrollHintConfig.maxScrollDistance,
    containerSize * scrollHintConfig.scrollDistanceRatio,
  )

  await animateScroll(container, 0, scrollDistance, direction)
  await animateScroll(container, scrollDistance, 0, direction)
}

function isVisibleEnough(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect()
  const visibleTop = Math.max(0, rect.top)
  const visibleBottom = Math.min(window.innerHeight, rect.bottom)
  const visibleHeight = Math.max(0, visibleBottom - visibleTop)
  const minPixels =
    window.innerWidth < 500
      ? scrollHintConfig.minVisiblePixelsNarrow
      : scrollHintConfig.minVisiblePixels
  const threshold = Math.min(
    minPixels,
    rect.height * scrollHintConfig.minVisibleRatio,
  )
  return visibleHeight >= threshold
}

function observeAndApplyScrollHint(
  container: HTMLElement,
  key: string,
  direction: ScrollHintDirection = 'horizontal',
): (() => void) | undefined {
  if (getScrolledKeys().has(key)) return

  let applied = false

  const cleanup = () => {
    resizeObserver.disconnect()
    intersectionObserver.disconnect()
  }

  const tryApply = () => {
    if (applied) return true
    if (!isVisibleEnough(container)) return false
    const hasOverflow =
      direction === 'vertical'
        ? container.scrollHeight > container.clientHeight
        : container.scrollWidth > container.clientWidth
    if (!hasOverflow) return false

    applied = true
    addScrolledKey(key)
    cleanup()
    applyScrollHint(container, direction)
    return true
  }

  const resizeObserver = new ResizeObserver(() => tryApply())
  resizeObserver.observe(container)

  const intersectionObserver = new IntersectionObserver(() => tryApply(), {
    threshold: [0, 0.5],
  })
  intersectionObserver.observe(container)

  const maxTimeout = setTimeout(cleanup, scrollHintConfig.maxWaitTime)

  return () => {
    clearTimeout(maxTimeout)
    cleanup()
  }
}

export function useScrollHint<T extends HTMLElement>({
  trigger,
  id,
  ref: externalRef,
  direction = 'horizontal',
}: UseScrollHintOptions<T>) {
  const internalRef = useRef<T>(null)
  const ref = externalRef ?? internalRef
  const routeKey = useRouteKey()
  const key = id ? `${routeKey}-${id}` : routeKey

  useEffect(() => {
    const container = ref.current
    if (!container || !trigger) return

    return observeAndApplyScrollHint(container, key, direction)
  }, [trigger, key, ref, direction])

  return ref
}

export function useScrollHintForElement({
  getElement,
  trigger,
  id,
  direction = 'horizontal',
}: UseScrollHintForElementOptions) {
  const routeKey = useRouteKey()
  const key = id ? `${routeKey}-${id}` : routeKey
  const getElementRef = useRef(getElement)
  getElementRef.current = getElement

  useEffect(() => {
    if (!trigger) return

    let observerCleanup: (() => void) | undefined

    const checkTimeout = setTimeout(() => {
      const container = getElementRef.current()
      if (!container) return

      observerCleanup = observeAndApplyScrollHint(container, key, direction)
    }, scrollHintConfig.renderDelay)

    return () => {
      clearTimeout(checkTimeout)
      observerCleanup?.()
    }
  }, [trigger, key])
}
